import React, { useEffect, useState } from "react";
import MaterialIcon from "./MaterialIcon";
import { useTranslation } from "../../i18n";

const BENEFIT_KEYS = [
  {
    id: "customAlerts",
    icon: "notifications_active",
    titleKey: "platformBenefits.customAlertsTitle",
    bodyKey: "platformBenefits.customAlertsBody",
    tone: "teal",
  },
  {
    id: "sentiment",
    icon: "psychology",
    titleKey: "platformBenefits.sentimentTitle",
    bodyKey: "platformBenefits.sentimentBody",
    tone: "navy",
  },
  {
    id: "localSeo",
    icon: "trending_up",
    titleKey: "platformBenefits.localSeoTitle",
    bodyKey: "platformBenefits.localSeoBody",
    tone: "mint",
  },
];

const TONE_STYLES = {
  teal: {
    iconWrap: "bg-[#006a61]/12 text-[#006a61]",
    accent: "from-[#006a61]/15 to-transparent",
    bar: "bg-[#006a61]",
  },
  navy: {
    iconWrap: "bg-[#0b1c30]/10 text-[#0b1c30]",
    accent: "from-[#0b1c30]/10 to-transparent",
    bar: "bg-[#0b1c30]",
  },
  mint: {
    iconWrap: "bg-[#c7ebe8] text-[#0b4f4a]",
    accent: "from-[#c7ebe8]/80 to-transparent",
    bar: "bg-[#2a9d8f]",
  },
};

const DISMISS_KEY = "repman_benefit_alerts_dismissed_v1";

/**
 * Professional platform benefit alerts for login + client dashboard.
 * variant: "auth" | "dashboard"
 */
const PlatformBenefitAlerts = ({
  variant = "dashboard",
  dismissible = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const [visibleIndex, setVisibleIndex] = useState(0);

  useEffect(() => {
    if (!dismissible || typeof window === "undefined") return;
    try {
      setDismissed(window.localStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      /* ignore */
    }
  }, [dismissible]);

  useEffect(() => {
    if (variant !== "auth") return undefined;
    const id = window.setInterval(() => {
      setVisibleIndex((i) => (i + 1) % BENEFIT_KEYS.length);
    }, 4500);
    return () => window.clearInterval(id);
  }, [variant]);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  if (dismissed) return null;

  if (variant === "auth") {
    const item = BENEFIT_KEYS[visibleIndex];
    const tone = TONE_STYLES[item.tone];
    return (
      <aside
        className={`overflow-hidden rounded-[20px] border border-white/60 bg-white/80 p-6 shadow-[0_16px_40px_rgba(11,28,48,0.08)] backdrop-blur-sm ${className}`}
        aria-live="polite"
      >
        <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.14em] text-[#64748b]">
          {t("platformBenefits.eyebrow")}
        </p>
        <div className="relative min-h-[148px]">
          {BENEFIT_KEYS.map((benefit, index) => {
            const active = index === visibleIndex;
            const styles = TONE_STYLES[benefit.tone];
            return (
              <div
                key={benefit.id}
                className={[
                  "absolute inset-0 transition-all duration-500 ease-out",
                  active
                    ? "translate-y-0 opacity-100"
                    : "pointer-events-none translate-y-2 opacity-0",
                ].join(" ")}
              >
                <div
                  className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${styles.iconWrap}`}
                >
                  <MaterialIcon name={benefit.icon} className="text-[24px]" />
                </div>
                <h3 className="text-[18px] font-bold leading-snug text-[#0b1c30]">
                  {t(benefit.titleKey)}
                </h3>
                <p className="mt-2 text-[14px] leading-relaxed text-[#64748b]">
                  {t(benefit.bodyKey)}
                </p>
              </div>
            );
          })}
        </div>
        <div className="mt-5 flex items-center gap-2">
          {BENEFIT_KEYS.map((benefit, index) => (
            <button
              key={benefit.id}
              type="button"
              aria-label={t(benefit.titleKey)}
              onClick={() => setVisibleIndex(index)}
              className={[
                "h-1.5 rounded-full transition-all",
                index === visibleIndex
                  ? `w-6 ${tone.bar}`
                  : "w-1.5 bg-[#cbd5e1] hover:bg-[#94a3b8]",
              ].join(" ")}
            />
          ))}
        </div>
      </aside>
    );
  }

  return (
    <section
      className={`mb-stack-lg overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-lowest shadow-soft ${className}`}
      aria-label={t("platformBenefits.eyebrow")}
    >
      <div className="flex items-start justify-between gap-4 border-b border-outline-variant/20 bg-gradient-to-r from-secondary/10 via-transparent to-transparent px-5 py-4 md:px-6">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-secondary">
            {t("platformBenefits.eyebrow")}
          </p>
          <h2 className="mt-1 font-headline-md text-headline-md text-primary">
            {t("platformBenefits.dashboardTitle")}
          </h2>
          <p className="mt-1 max-w-2xl font-body-md text-body-md text-on-surface-variant">
            {t("platformBenefits.dashboardSubtitle")}
          </p>
        </div>
        {dismissible ? (
          <button
            type="button"
            onClick={handleDismiss}
            className="shrink-0 rounded-lg p-2 text-outline transition hover:bg-surface-container hover:text-on-surface"
            aria-label={t("platformBenefits.dismiss")}
          >
            <MaterialIcon name="close" className="text-[20px]" />
          </button>
        ) : null}
      </div>

      <ul className="grid grid-cols-1 gap-0 md:grid-cols-3 md:divide-x md:divide-outline-variant/20">
        {BENEFIT_KEYS.map((benefit) => {
          const styles = TONE_STYLES[benefit.tone];
          return (
            <li
              key={benefit.id}
              className={`relative px-5 py-5 md:px-6 bg-gradient-to-br ${styles.accent}`}
            >
              <div
                className={`mb-3 flex h-11 w-11 items-center justify-center rounded-xl ${styles.iconWrap}`}
              >
                <MaterialIcon name={benefit.icon} className="text-[22px]" />
              </div>
              <h3 className="font-headline-md text-[16px] font-bold text-primary">
                {t(benefit.titleKey)}
              </h3>
              <p className="mt-2 font-body-md text-[14px] leading-relaxed text-on-surface-variant">
                {t(benefit.bodyKey)}
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default PlatformBenefitAlerts;
